import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { EntityType, TripStatus, TripType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { Trip } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import localSettings from "@app/config/LocalSettings";
import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import {
  getFieldTimeDrillDownTotalFilteredRecords,
  getFieldTimeDrillDownViewState,
} from "@app/scene/dashboard/store/cards/fieldTime/selectors";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import { callApi } from "@app/store/api/callApi";
import { fetchFieldTimeCardDataHelper } from "@app/store/dashboard/cardDataFetchHelpers";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import { getCurrentUserId, getOrganizationId } from "@app/store/iam";
import { getTeamsOfEntireOrganization } from "@app/store/members";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import tripFieldModel, { TripFieldName } from "@app/util/fieldModel/tripFieldModel";
import {
  createAndCondition,
  createEmptyFilterModel,
  createSimpleCondition,
} from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import { takeLatestBy } from "@app/util/redux/takeLatestBy";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import getDefaultDrillDownColumns from "../../utils/getDefaultDrillDownColumns";

import {
  applyFieldTimeDrillDownListViewSettings,
  exportFieldTimeCardDrillDownData,
  fetchFieldTimeCardData,
  fetchFieldTimeCardDrillDownData,
  showFieldTimeDrillDown,
  updateFieldTimeTrip,
} from "./actions";

// Field Time's drill-down lists the field trips behind the metric (drive time = trip duration).
const defaultColumns: string[] = [
  TripFieldName.START_TIME,
  TripFieldName.USER,
  TripFieldName.START,
  TripFieldName.FINISH,
  TripFieldName.DISTANCE,
  TripFieldName.DURATION,
];

const defaultSortColumn = TripFieldName.START_TIME;

export function* onFetchFieldTimeCardData({ payload }: ReturnType<typeof fetchFieldTimeCardData>) {
  const { callback, configuration, failureCallback, scope } = payload;

  try {
    yield call(fetchFieldTimeCardDataHelper, { callback, configuration, scope });
  } catch (error) {
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

function* onOpenDrillDown({ payload }: ReturnType<typeof showFieldTimeDrillDown.request>) {
  const { configuration, customDateRange, scope } = payload;
  const { dateRange } = configuration;

  const drillDownViewState: DrillDownViewState = yield select(getFieldTimeDrillDownViewState);

  const viewState = localSettings.getViewSettings(`dashboard/fieldTime`, tripFieldModel, {
    ...drillDownViewState,
    columns: getDefaultDrillDownColumns(defaultColumns, tripFieldModel),
    sort: [{ field: tripFieldModel.getByName(defaultSortColumn)!, order: SortOrder.DESC }],
  });

  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const isUserScope = !scope?.teamId && !!scope?.userId;
  const range = customDateRange ?? getDatesRange(dateRange.range, dateRange.subRange);

  const filter: FilterModel = createAndCondition([
    createSimpleCondition(TripFieldName.START_TIME, FilterOperator.IN_RANGE, [
      range.startDate,
      range.endDate,
    ]),
  ]);

  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  if (isTeamScope) {
    filter.conditions.push(
      createSimpleCondition(
        CommonFieldName.TEAM,
        FilterOperator.IN_ANY,
        teams.find(({ id }) => id === scope.teamId)?.users.map(({ id }) => id) ?? []
      )
    );
  }
  if (scope?.userId) {
    filter.conditions.push(
      createSimpleCondition(TripFieldName.USER, FilterOperator.IN_ANY, [scope.userId])
    );
  }

  // Only business trips count toward another user's field time (a user's own field time
  // may include personal trips). The current user id is only needed to make that check for
  // single-user scope, so it is fetched only then.
  let isViewingOwnData = false;
  if (isUserScope) {
    const currentUserId: User["id"] = yield select(getCurrentUserId);
    isViewingOwnData = scope?.userId === currentUserId;
  }
  if (!isViewingOwnData) {
    filter.conditions.push(
      createSimpleCondition(TripFieldName.TYPE, FilterOperator.EQUALS, TripType.BUSINESS)
    );
  }

  viewState.filter = filter;
  yield put(showFieldTimeDrillDown.success({ viewState }));
}

export function* onFetchDrillDownData({
  payload,
}: ReturnType<typeof fetchFieldTimeCardDrillDownData.request>) {
  try {
    loggingService.debug("Dashboard: field time card, onFetchDrillDownData", payload);
    // Clone before stripping — the dispatched action payload must never be mutated.
    const request = { ...payload.request };
    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete request.filter;
    }

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyFieldTimeDrillDownListViewSettings(request));
    }

    const drillDownViewState: DrillDownViewState = yield select(getFieldTimeDrillDownViewState);

    if (!payload.fetchOnlyWithoutFilters) {
      localSettings.setViewSettings(drillDownViewState, `dashboard/fieldTime`);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.startRow
        : drillDownViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : drillDownViewState.range.endRow - drillDownViewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);

    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        status: TripStatus.COMPLETED,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : drillDownViewState.filter,
          drillDownViewState.columns,
          tripFieldModel
        ),
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(drillDownViewState.sort),
    };

    const response: ListResponse<Trip> = yield callApi("fetchTrips", orgId, requestPayload);
    if (payload.dataCallback) {
      yield call(payload.dataCallback, response);
    }
    yield put(
      fetchFieldTimeCardDrillDownData.success({
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    if (payload.failCallback) {
      yield call(payload.failCallback);
    }
    yield put(fetchFieldTimeCardDrillDownData.failure());
    yield put(handleError({ error }));
  }
}

function* onExport() {
  const drillDownViewState: DrillDownViewState = yield select(getFieldTimeDrillDownViewState);
  const total: number = yield select(getFieldTimeDrillDownTotalFilteredRecords);

  yield put(
    exportEntities.request({
      entityType: EntityType.TRIP,
      total,
      viewState: drillDownViewState,
    })
  );
}

function* onUpdateTrip({ payload }: ReturnType<typeof updateFieldTimeTrip.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const updatedTrip: Trip = yield callApi("updateTrip", orgId, payload.entity);
    if (payload.onSuccess) {
      yield call(payload.onSuccess, updatedTrip);
    }
    yield put(updateFieldTimeTrip.success(updatedTrip));
    yield put(notifyAboutChanges({ entityType: EntityType.TRIP, updated: [updatedTrip] }));
  } catch (error) {
    if (payload.onFailure) {
      yield call(payload.onFailure);
    }
    yield put(updateFieldTimeTrip.failure());
    yield put(handleError({ error }));
  }
}

export function* fieldTimeSagas() {
  // takeLatestBy (keyed by cardId) because there may be several cards of this type on a board:
  // cancel a stale in-flight fetch for the same card while keeping other cards independent.
  yield takeLatestBy(
    fetchFieldTimeCardData,
    onFetchFieldTimeCardData,
    (action: ReturnType<typeof fetchFieldTimeCardData>) => action.payload.cardId
  );
  yield takeLatest(showFieldTimeDrillDown.request, onOpenDrillDown);
  yield takeLatest(fetchFieldTimeCardDrillDownData.request, onFetchDrillDownData);
  yield takeLatest(exportFieldTimeCardDrillDownData, onExport);
  yield takeEvery(updateFieldTimeTrip.request, onUpdateTrip);
}
